import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import ProtectedRoute from "@/components/ProtectedRoute";
import RoleGate from "@/components/RoleGate";
import Table from "@/components/Base/Table";
import Button from "@/components/Base/Button";
import { FormInput, FormSelect } from "@/components/Base/Form";
import Pagination from "@/components/Base/Pagination";
import Lucide from "@/components/Base/Lucide";
import Litepicker from "@/components/Base/Litepicker";
import { getToken } from "@/utils/auth";

type Distributor = { id: string; name: string };
type Product = { id: string; name: string; sku?: string | null };
type LedgerRow = {
  id: string;
  accrualDate: string;
  status: string;
  qty: number;
  claimedQty: number;
  outstandingQty: number;
  rebatePerUnitSnapshot: string;
  rebateTotalSnapshot: string;
  claimedAmount: string;
  outstandingAmount: string;
  distributor: { id: string; name: string; code?: string | null };
  product: { id: string; name: string; sku?: string | null; type?: string | null };
  stockInReceipt: { id: string; batchNo: string; invoiceNo?: string | null; refNo?: string | null };
};
type ClaimDocRow = {
  id: string;
  claimRef: string;
  claimDate: string;
  status: string;
  lineCount: number;
  totalClaimedAmount: string;
  settledAmount: string;
  outstandingAmount: string;
  externalInvoiceRef?: string | null;
  externalInvoiceDate?: string | null;
  financeSubmittedAt?: string | null;
  settlementNote?: string | null;
  distributor: { id: string; name: string; code?: string | null };
};

function fmtDate(v: string) {
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "-";
  return new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", year: "numeric" }).format(d);
}

function to2(v: string | number) {
  const n = Number(v || 0);
  if (!Number.isFinite(n)) return "0.00";
  return n.toFixed(2);
}

function dateToYMD(v: Date) {
  return v.toISOString().slice(0, 10);
}

function parseDateRangeInput(value: string): { startDate: string; endDate: string } | null {
  const m = value.trim().match(/(\d{4}-\d{2}-\d{2})\s*-\s*(\d{4}-\d{2}-\d{2})/);
  if (!m) return null;
  return { startDate: m[1], endDate: m[2] };
}

function SectionInfo({ text }: { text: string }) {
  return (
    <span className="relative inline-flex">
      <button
        type="button"
        className="peer inline-flex items-center justify-center w-5 h-5 rounded-full border border-slate-300 text-slate-500 hover:text-primary hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
        aria-label="Show help information"
      >
        <Lucide icon="Info" className="w-3.5 h-3.5" />
      </button>
      <span className="pointer-events-none absolute z-30 hidden peer-hover:block peer-focus-visible:block left-1/2 -translate-x-1/2 top-7 w-72 rounded-md border border-slate-200 bg-white px-3 py-2 text-[11px] leading-4 text-slate-600 shadow-lg">
        {text}
      </span>
    </span>
  );
}

export default function RebateLedgerPage() {
  const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";
  const token = getToken();
  const today = new Date();
  const monthStart = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1));
  const defaultStart = dateToYMD(monthStart);
  const defaultEnd = dateToYMD(today);

  const [distributors, setDistributors] = useState<Distributor[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [rows, setRows] = useState<LedgerRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);

  const [distributorId, setDistributorId] = useState("");
  const [productId, setProductId] = useState("");
  const [status, setStatus] = useState("");
  const [dateRange, setDateRange] = useState(`${defaultStart} - ${defaultEnd}`);

  const [selectedIds, setSelectedIds] = useState<Record<string, boolean>>({});
  const [claimQtyById, setClaimQtyById] = useState<Record<string, string>>({});
  const [claimRef, setClaimRef] = useState("");
  const [claimDate, setClaimDate] = useState(new Date().toISOString().slice(0, 10));
  const [claimNote, setClaimNote] = useState("");
  const [claiming, setClaiming] = useState(false);
  const [claimDocs, setClaimDocs] = useState<ClaimDocRow[]>([]);
  const [workflowClaimId, setWorkflowClaimId] = useState("");
  const [workflowAction, setWorkflowAction] = useState("SEND_TO_FINANCE");
  const [workflowInvoiceRef, setWorkflowInvoiceRef] = useState("");
  const [workflowInvoiceDate, setWorkflowInvoiceDate] = useState("");
  const [workflowSettledAmount, setWorkflowSettledAmount] = useState("");
  const [workflowNote, setWorkflowNote] = useState("");
  const [workflowSubmitting, setWorkflowSubmitting] = useState(false);

  const selectedRows = useMemo(() => rows.filter((r) => selectedIds[r.id]), [rows, selectedIds]);
  const selectedOutstandingTotal = useMemo(
    () => selectedRows.reduce((sum, r) => sum + Number(r.outstandingAmount || 0), 0),
    [selectedRows]
  );
  const selectedClaimQtyTotal = useMemo(
    () =>
      selectedRows.reduce((sum, r) => {
        const q = Number(claimQtyById[r.id] || 0);
        return sum + (Number.isFinite(q) ? Math.max(Math.floor(q), 0) : 0);
      }, 0),
    [selectedRows, claimQtyById]
  );
  const selectedClaimAmountTotal = useMemo(
    () =>
      selectedRows.reduce((sum, r) => {
        const q = Number(claimQtyById[r.id] || 0);
        const qty = Number.isFinite(q) ? Math.max(Math.floor(q), 0) : 0;
        const rate = Number(r.rebatePerUnitSnapshot || 0);
        return sum + qty * (Number.isFinite(rate) ? rate : 0);
      }, 0),
    [selectedRows, claimQtyById]
  );

  const loadBaseData = async () => {
    const [dRes, pRes] = await Promise.all([
      axios.get(`${apiUrl}/distributors?pageSize=500`, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${apiUrl}/products?pageSize=500`, { headers: { Authorization: `Bearer ${token}` } }),
    ]);
    setDistributors(Array.isArray(dRes.data?.data) ? dRes.data.data : []);
    setProducts(Array.isArray(pRes.data?.data) ? pRes.data.data : []);
  };

  const loadLedger = async () => {
    setLoading(true);
    setError("");
    try {
      const parsedRange = parseDateRangeInput(dateRange);
      if (!parsedRange) {
        setError("Please select a valid date range.");
        setRows([]);
        setTotal(0);
        setLoading(false);
        return;
      }
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("pageSize", String(pageSize));
      if (distributorId) params.set("distributorId", distributorId);
      if (productId) params.set("productId", productId);
      if (status) params.set("status", status);
      params.set("startDate", parsedRange.startDate);
      params.set("endDate", parsedRange.endDate);
      const res = await axios.get(`${apiUrl}/rebates/ledger?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = Array.isArray(res.data?.data) ? res.data.data : [];
      setRows(data);
      setTotal(Number(res.data?.total || 0));
      setSelectedIds({});
      const nextClaimQty: Record<string, string> = {};
      for (const r of data) nextClaimQty[r.id] = String(Math.max(Number(r.outstandingQty || 0), 0));
      setClaimQtyById(nextClaimQty);
    } catch {
      setError("Failed to load rebate ledger.");
    } finally {
      setLoading(false);
    }
  };

  const loadClaimDocs = async () => {
    try {
      const parsedRange = parseDateRangeInput(dateRange);
      if (!parsedRange) return;
      const params = new URLSearchParams();
      params.set("page", "1");
      params.set("pageSize", "200");
      if (distributorId) params.set("distributorId", distributorId);
      params.set("startDate", parsedRange.startDate);
      params.set("endDate", parsedRange.endDate);
      const res = await axios.get(`${apiUrl}/rebates/claims?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = Array.isArray(res.data?.data) ? res.data.data : [];
      setClaimDocs(data);
      if (!workflowClaimId && data[0]?.id) setWorkflowClaimId(data[0].id);
    } catch {
      // keep ledger usable
    }
  };

  useEffect(() => {
    void loadBaseData();
  }, []);

  useEffect(() => {
    void loadLedger();
    void loadClaimDocs();
  }, [page, pageSize]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const handleSearch = () => {
    setPage(1);
    void loadLedger();
    void loadClaimDocs();
  };

  const handleExport = async () => {
    try {
      const parsedRange = parseDateRangeInput(dateRange);
      if (!parsedRange) {
        setError("Please select a valid date range.");
        return;
      }
      const params = new URLSearchParams();
      if (distributorId) params.set("distributorId", distributorId);
      if (productId) params.set("productId", productId);
      if (status) params.set("status", status);
      params.set("startDate", parsedRange.startDate);
      params.set("endDate", parsedRange.endDate);

      const res = await axios.get(`${apiUrl}/rebates/ledger/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rebate-ledger-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError("Failed to export ledger.");
    }
  };

  const handleCreateClaim = async () => {
    if (!selectedRows.length) {
      setError("Select at least one ledger row.");
      return;
    }
    for (const r of selectedRows) {
      const q = Math.floor(Number(claimQtyById[r.id] || 0));
      if (!Number.isFinite(q) || q <= 0) {
        setError(`Claim qty must be > 0 for ${r.product.name}.`);
        return;
      }
      if (q > Number(r.outstandingQty || 0)) {
        setError(`Claim qty exceeds balance qty for ${r.product.name}.`);
        return;
      }
    }
    const distinctDistributors = Array.from(new Set(selectedRows.map((r) => r.distributor.id)));
    if (distinctDistributors.length !== 1) {
      setError("Please select rows from one distributor only.");
      return;
    }
    setClaiming(true);
    setError("");
    setSuccess("");
    try {
      await axios.post(
        `${apiUrl}/rebates/claims`,
        {
          claimRef: claimRef.trim() || undefined,
          claimDate,
          distributorId: distinctDistributors[0],
          notes: claimNote,
          items: selectedRows.map((r) => ({
            rebateAccrualId: r.id,
            claimedQty: Math.floor(Number(claimQtyById[r.id] || 0)),
          })),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess("Claim created successfully.");
      setClaimRef("");
      setClaimNote("");
      setSelectedIds({});
      await loadLedger();
      await loadClaimDocs();
    } catch {
      setError("Failed to create claim.");
    } finally {
      setClaiming(false);
    }
  };

  const handleWorkflowUpdate = async () => {
    if (!workflowClaimId) {
      setError("Please select a claim document.");
      return;
    }
    setWorkflowSubmitting(true);
    setError("");
    setSuccess("");
    try {
      await axios.patch(
        `${apiUrl}/rebates/claims/${workflowClaimId}/workflow`,
        {
          action: workflowAction,
          externalInvoiceRef: workflowInvoiceRef.trim() || undefined,
          externalInvoiceDate: workflowInvoiceDate || undefined,
          settledAmount:
            workflowSettledAmount.trim() === "" ? undefined : Number(workflowSettledAmount),
          settlementNote: workflowNote.trim() || undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess("Claim workflow updated.");
      await loadLedger();
      await loadClaimDocs();
    } catch {
      setError("Failed to update claim workflow.");
    } finally {
      setWorkflowSubmitting(false);
    }
  };

  return (
    <ProtectedRoute>
      <RoleGate allow={["SUPERADMIN", "ADMIN"]}>
        <div className="p-6">
          <div className="flex items-center gap-2">
            {loading ? <Lucide icon="Loader2" className="w-4 h-4 animate-spin text-slate-500" /> : null}
          </div>

          <div className="box p-4 mt-4 grid grid-cols-1 md:grid-cols-6 gap-3">
            <div className="md:col-span-6">
              <div className="font-medium text-sm flex items-center gap-2">
                <span>1) Filter Rebate Accrual Ledger</span>
                <SectionInfo text="Choose distributor/date range first. If no rows appear, expand date range and click Filter." />
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1">
                Distributor
              </div>
              <FormSelect value={distributorId} onChange={(e) => setDistributorId(e.target.value)}>
                <option value="">All Distributors</option>
                {distributors.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </FormSelect>
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1">
                Product
              </div>
              <FormSelect value={productId} onChange={(e) => setProductId(e.target.value)}>
                <option value="">All Products</option>
                {products.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} {p.sku ? `(${p.sku})` : ""}
                  </option>
                ))}
              </FormSelect>
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1">
                Accrual Status
              </div>
              <FormSelect value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">All Status</option>
                <option value="ACCRUED">ACCRUED</option>
                <option value="PARTIALLY_CLAIMED">PARTIALLY_CLAIMED</option>
                <option value="CLAIMED">CLAIMED</option>
              </FormSelect>
            </div>
            <div className="md:col-span-2">
              <div className="text-xs text-slate-500 mb-1">
                Date Range
              </div>
              <div className="relative inline-flex w-full">
                <Lucide
                  icon="Calendar"
                  className="absolute inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3] text-slate-500"
                />
                <Litepicker
                  value={dateRange}
                  onChange={(e) => setDateRange((e.target?.value || "").trim())}
                  options={{
                    autoApply: true,
                    singleMode: false,
                    numberOfColumns: 2,
                    numberOfMonths: 2,
                    showWeekNumbers: true,
                    dropdowns: {
                      minYear: 2020,
                      maxYear: null,
                      months: true,
                      years: true,
                    },
                    format: "YYYY-MM-DD",
                  }}
                  className="w-full min-w-[220px] pl-9 pr-3 py-2 rounded-[0.5rem] text-sm"
                />
              </div>
            </div>
            <div className="flex gap-2 md:items-end">
              <Button variant="primary" onClick={handleSearch}>
                Filter
              </Button>
              <Button variant="outline-secondary" onClick={handleExport}>
                Export Excel
              </Button>
            </div>
          </div>

          <div className="box p-4 mt-4">
            <div className="font-medium text-sm mb-1 flex items-center gap-2">
              <span>2) Create Claim Request (for Finance)</span>
              <SectionInfo text="Select ledger rows from one distributor in the table below, then create one claim document." />
            </div>
            <div className="flex flex-wrap gap-2 items-end">
              <div>
                <div className="text-xs text-slate-500 mb-1">
                  Claim Ref
                </div>
                <FormInput
                  type="text"
                  placeholder="Claim Ref (optional)"
                  value={claimRef}
                  onChange={(e) => setClaimRef(e.target.value)}
                  className="w-56"
                />
              </div>
              <div>
                <div className="text-xs text-slate-500 mb-1 whitespace-nowrap">
                  Claim Date
                </div>
                <div className="relative inline-flex w-44">
                  <Lucide
                    icon="Calendar"
                    className="absolute inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3] text-slate-500"
                  />
                  <Litepicker
                    value={claimDate}
                    onChange={(e) => setClaimDate((e.target?.value || "").trim())}
                    options={{
                      autoApply: true,
                      singleMode: true,
                      numberOfColumns: 1,
                      numberOfMonths: 1,
                      dropdowns: {
                        minYear: 2020,
                        maxYear: null,
                        months: true,
                        years: true,
                      },
                      format: "YYYY-MM-DD",
                    }}
                    className="w-full pl-9 pr-3 py-2 rounded-[0.5rem] text-sm"
                  />
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500 mb-1">
                  Claim Note
                </div>
                <FormInput
                  type="text"
                  placeholder="Claim note"
                  value={claimNote}
                  onChange={(e) => setClaimNote(e.target.value)}
                  className="w-64"
                />
              </div>
              <Button variant="success" className="self-end" onClick={handleCreateClaim} disabled={claiming || !selectedRows.length}>
                Mark Selected as Claimed
              </Button>
              <div className="text-sm text-slate-500 self-end pb-1">
                Selected: {selectedRows.length} • Claim Qty: {selectedClaimQtyTotal} • Claim Amount: RM {to2(selectedClaimAmountTotal)} • Total Outstanding: RM {to2(selectedOutstandingTotal)}
              </div>
            </div>
          </div>

          <div className="box p-4 mt-4">
            <div className="text-sm font-medium mb-1 flex items-center gap-2">
              <span>3) Finance Handoff / Claim Workflow Update</span>
              <SectionInfo text="Update claim lifecycle after finance processes it in external accounting software." />
            </div>
            <div className="flex flex-wrap gap-2 items-end">
              <div>
                <div className="text-xs text-slate-500 mb-1">
                  Claim Document
                </div>
                <FormSelect value={workflowClaimId} onChange={(e) => setWorkflowClaimId(e.target.value)} className="w-[320px]">
                  <option value="">Select Claim Document</option>
                  {claimDocs.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.claimRef} - {c.distributor.name} ({to2(c.totalClaimedAmount)})
                    </option>
                  ))}
                </FormSelect>
              </div>
              <div>
                <div className="text-xs text-slate-500 mb-1">
                  Action
                </div>
                <FormSelect value={workflowAction} onChange={(e) => setWorkflowAction(e.target.value)} className="w-[220px]">
                  <option value="SEND_TO_FINANCE">Send to Finance</option>
                  <option value="MARK_PARTIALLY_SETTLED">Mark Partially Settled</option>
                  <option value="MARK_SETTLED">Mark Settled</option>
                  <option value="CANCEL">Cancel Claim</option>
                </FormSelect>
              </div>
              <div>
                <div className="text-xs text-slate-500 mb-1">
                  External Invoice Ref
                </div>
                <FormInput
                  type="text"
                  placeholder="External Invoice Ref"
                  value={workflowInvoiceRef}
                  onChange={(e) => setWorkflowInvoiceRef(e.target.value)}
                  className="w-52"
                />
              </div>
              <div>
                <div className="text-xs text-slate-500 mb-1 whitespace-nowrap">
                  External Invoice Date
                </div>
                <div className="relative inline-flex w-44">
                  <Lucide
                    icon="Calendar"
                    className="absolute inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3] text-slate-500"
                  />
                  <Litepicker
                    value={workflowInvoiceDate}
                    onChange={(e) => setWorkflowInvoiceDate((e.target?.value || "").trim())}
                    options={{
                      autoApply: true,
                      singleMode: true,
                      numberOfColumns: 1,
                      numberOfMonths: 1,
                      dropdowns: {
                        minYear: 2020,
                        maxYear: null,
                        months: true,
                        years: true,
                      },
                      format: "YYYY-MM-DD",
                    }}
                    className="w-full pl-9 pr-3 py-2 rounded-[0.5rem] text-sm"
                  />
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500 mb-1">
                  Settled Amount (RM)
                </div>
                <FormInput
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Settled Amount (RM)"
                  value={workflowSettledAmount}
                  onChange={(e) => setWorkflowSettledAmount(e.target.value)}
                  className="w-44"
                />
              </div>
              <div>
                <div className="text-xs text-slate-500 mb-1">
                  Workflow Note
                </div>
                <FormInput
                  type="text"
                  placeholder="Settlement / workflow note"
                  value={workflowNote}
                  onChange={(e) => setWorkflowNote(e.target.value)}
                  className="w-72"
                />
              </div>
              <Button variant="primary" className="self-end" onClick={handleWorkflowUpdate} disabled={workflowSubmitting}>
                Update Claim Workflow
              </Button>
            </div>
          </div>

          {error ? <div className="mt-4 text-danger text-sm">{error}</div> : null}
          {success ? <div className="mt-4 text-success text-sm">{success}</div> : null}

          <div className="box mt-4 overflow-x-auto">
            <div className="px-4 pt-4 pb-1 text-sm font-medium">4) Rebate Accrual Ledger Rows (from Stock IN)</div>
            <Table className="border-b border-slate-200/60 text-xs table-auto w-max min-w-full">
              <Table.Thead className="text-xs bg-white dark:bg-darkmode-600">
                <Table.Tr>
                  <Table.Td className="py-2 px-2 font-medium bg-slate-50 dark:bg-darkmode-700 min-w-[48px] text-center">Sel</Table.Td>
                  <Table.Td className="py-2 px-2 font-medium bg-slate-50 dark:bg-darkmode-700 min-w-[110px]">Date</Table.Td>
                  <Table.Td className="py-2 px-2 font-medium bg-slate-50 dark:bg-darkmode-700 min-w-[170px]">Distributor</Table.Td>
                  <Table.Td className="py-2 px-2 font-medium bg-slate-50 dark:bg-darkmode-700 min-w-[190px]">Product</Table.Td>
                  <Table.Td className="py-2 px-2 font-medium bg-success/10 dark:bg-success/10 min-w-[70px] text-right">Qty</Table.Td>
                  <Table.Td className="py-2 px-2 font-medium bg-success/10 dark:bg-success/10 min-w-[90px] text-right">Claimed Qty</Table.Td>
                  <Table.Td className="py-2 px-2 font-medium bg-success/10 dark:bg-success/10 min-w-[90px] text-right">Balance Qty</Table.Td>
                  <Table.Td className="py-2 px-2 font-medium bg-primary/10 dark:bg-primary/10 min-w-[110px] text-center">Claim Qty</Table.Td>
                  <Table.Td className="py-2 px-2 font-medium bg-success/10 dark:bg-success/10 min-w-[90px] text-right">Rate</Table.Td>
                  <Table.Td className="py-2 px-2 font-medium bg-warning/10 dark:bg-warning/10 min-w-[100px] text-right">Accrued</Table.Td>
                  <Table.Td className="py-2 px-2 font-medium bg-warning/10 dark:bg-warning/10 min-w-[100px] text-right">Claimed</Table.Td>
                  <Table.Td className="py-2 px-2 font-medium bg-warning/10 dark:bg-warning/10 min-w-[110px] text-right">Outstanding</Table.Td>
                  <Table.Td className="py-2 px-2 font-medium bg-slate-50 dark:bg-darkmode-700 min-w-[140px]">Status</Table.Td>
                  <Table.Td className="py-2 px-2 font-medium bg-slate-50 dark:bg-darkmode-700 min-w-[120px]">Stock In Ref</Table.Td>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((r) => (
                  <Table.Tr key={r.id} className="[&_td]:border-b [&_td]:border-slate-200/60">
                    <Table.Td className="py-2 px-2 text-center">
                      <input
                        type="checkbox"
                        checked={!!selectedIds[r.id]}
                        disabled={Number(r.outstandingQty) <= 0}
                        onChange={(e) => setSelectedIds((prev) => ({ ...prev, [r.id]: e.target.checked }))}
                      />
                    </Table.Td>
                    <Table.Td className="py-2 px-2 whitespace-nowrap">{fmtDate(r.accrualDate)}</Table.Td>
                    <Table.Td className="py-2 px-2 whitespace-nowrap">{r.distributor.name}</Table.Td>
                    <Table.Td className="py-2 px-2 max-w-[300px] truncate">{r.product.name}</Table.Td>
                    <Table.Td className="py-2 px-2 text-right tabular-nums bg-success/10 dark:bg-success/10">{r.qty}</Table.Td>
                    <Table.Td className="py-2 px-2 text-right tabular-nums bg-success/10 dark:bg-success/10">{r.claimedQty}</Table.Td>
                    <Table.Td className="py-2 px-2 text-right tabular-nums bg-success/10 dark:bg-success/10">{r.outstandingQty}</Table.Td>
                    <Table.Td className="py-2 px-2 text-center tabular-nums bg-primary/10 dark:bg-primary/10">
                      <FormInput
                        type="number"
                        min={0}
                        step={1}
                        className="w-[88px] text-center h-7 text-xs"
                        value={claimQtyById[r.id] ?? String(r.outstandingQty ?? 0)}
                        disabled={!selectedIds[r.id] || Number(r.outstandingQty) <= 0}
                        onChange={(e) =>
                          setClaimQtyById((prev) => ({
                            ...prev,
                            [r.id]: e.target.value.replace(/[^\d]/g, ""),
                          }))
                        }
                        onBlur={(e) => {
                          const raw = Number(e.target.value || 0);
                          const normalized = Math.max(0, Math.min(Math.floor(raw), Number(r.outstandingQty || 0)));
                          setClaimQtyById((prev) => ({ ...prev, [r.id]: String(normalized) }));
                        }}
                      />
                    </Table.Td>
                    <Table.Td className="py-2 px-2 text-right tabular-nums bg-success/10 dark:bg-success/10">{to2(r.rebatePerUnitSnapshot)}</Table.Td>
                    <Table.Td className="py-2 px-2 text-right tabular-nums bg-warning/10 dark:bg-warning/10">{to2(r.rebateTotalSnapshot)}</Table.Td>
                    <Table.Td className="py-2 px-2 text-right tabular-nums bg-warning/10 dark:bg-warning/10">{to2(r.claimedAmount)}</Table.Td>
                    <Table.Td className="py-2 px-2 text-right tabular-nums bg-warning/10 dark:bg-warning/10">{to2(r.outstandingAmount)}</Table.Td>
                    <Table.Td className="py-2 px-2 whitespace-nowrap">{r.status}</Table.Td>
                    <Table.Td className="py-2 px-2 whitespace-nowrap">{r.stockInReceipt.batchNo}</Table.Td>
                  </Table.Tr>
                ))}
                {!rows.length ? (
                  <Table.Tr>
                    <Table.Td colSpan={14} className="text-center text-slate-500 py-6">
                      No ledger rows found. Try a wider Date Range and click Filter.
                    </Table.Td>
                  </Table.Tr>
                ) : null}
              </Table.Tbody>
            </Table>
          </div>

          <div className="box mt-4 overflow-x-auto">
            <div className="px-4 pt-4 pb-1 text-sm font-medium">5) Claim Documents Summary</div>
            <Table className="border-b border-slate-200/60 text-xs table-auto w-max min-w-full">
              <Table.Thead className="text-xs bg-white dark:bg-darkmode-600">
                <Table.Tr>
                  <Table.Td className="py-2 px-2 font-medium bg-slate-50 dark:bg-darkmode-700 min-w-[160px]">Claim Ref</Table.Td>
                  <Table.Td className="py-2 px-2 font-medium bg-slate-50 dark:bg-darkmode-700 min-w-[110px]">Claim Date</Table.Td>
                  <Table.Td className="py-2 px-2 font-medium bg-slate-50 dark:bg-darkmode-700 min-w-[170px]">Distributor</Table.Td>
                  <Table.Td className="py-2 px-2 font-medium bg-warning/10 dark:bg-warning/10 min-w-[110px] text-right">Claimed</Table.Td>
                  <Table.Td className="py-2 px-2 font-medium bg-warning/10 dark:bg-warning/10 min-w-[110px] text-right">Settled</Table.Td>
                  <Table.Td className="py-2 px-2 font-medium bg-warning/10 dark:bg-warning/10 min-w-[110px] text-right">Outstanding</Table.Td>
                  <Table.Td className="py-2 px-2 font-medium bg-slate-50 dark:bg-darkmode-700 min-w-[150px]">Status</Table.Td>
                  <Table.Td className="py-2 px-2 font-medium bg-slate-50 dark:bg-darkmode-700 min-w-[150px]">External Invoice Ref</Table.Td>
                  <Table.Td className="py-2 px-2 font-medium bg-slate-50 dark:bg-darkmode-700 min-w-[120px]">External Invoice Date</Table.Td>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {claimDocs.map((c) => (
                  <Table.Tr key={c.id} className="[&_td]:border-b [&_td]:border-slate-200/60">
                    <Table.Td className="py-2 px-2 whitespace-nowrap">{c.claimRef}</Table.Td>
                    <Table.Td className="py-2 px-2 whitespace-nowrap">{fmtDate(c.claimDate)}</Table.Td>
                    <Table.Td className="py-2 px-2 whitespace-nowrap">{c.distributor.name}</Table.Td>
                    <Table.Td className="py-2 px-2 text-right tabular-nums bg-warning/10 dark:bg-warning/10">{to2(c.totalClaimedAmount)}</Table.Td>
                    <Table.Td className="py-2 px-2 text-right tabular-nums bg-warning/10 dark:bg-warning/10">{to2(c.settledAmount)}</Table.Td>
                    <Table.Td className="py-2 px-2 text-right tabular-nums bg-warning/10 dark:bg-warning/10">{to2(c.outstandingAmount)}</Table.Td>
                    <Table.Td className="py-2 px-2 whitespace-nowrap">{c.status}</Table.Td>
                    <Table.Td className="py-2 px-2 whitespace-nowrap">{c.externalInvoiceRef || "-"}</Table.Td>
                    <Table.Td className="py-2 px-2 whitespace-nowrap">{c.externalInvoiceDate ? fmtDate(c.externalInvoiceDate) : "-"}</Table.Td>
                  </Table.Tr>
                ))}
                {!claimDocs.length ? (
                  <Table.Tr>
                    <Table.Td colSpan={9} className="text-center text-slate-500 py-6">
                      No claim documents found for selected filters/date range.
                    </Table.Td>
                  </Table.Tr>
                ) : null}
              </Table.Tbody>
            </Table>
          </div>

          <div className="flex items-center gap-3 mt-4">
            <Pagination>
              <Pagination.Link onClick={() => setPage((p) => Math.max(1, p - 1))}>
                <Lucide icon="ChevronLeft" className="w-4 h-4" />
              </Pagination.Link>
              <Pagination.Link active>{page}</Pagination.Link>
              <Pagination.Link onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
                <Lucide icon="ChevronRight" className="w-4 h-4" />
              </Pagination.Link>
            </Pagination>
            <FormSelect
              className="w-24"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
            >
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </FormSelect>
            <div className="text-sm text-slate-500">Total: {total}</div>
          </div>
        </div>
      </RoleGate>
    </ProtectedRoute>
  );
}

